/**
 * UI Components for CompanyInfoStep
 */

import { type ChangeEvent } from 'react'

// =============================================================================
// SaveStatusIndicator
// =============================================================================

interface SaveStatusIndicatorProps {
  status: 'idle' | 'saving' | 'saved'
}

function SavingIcon(): JSX.Element {
  return (
    <svg className="animate-spin h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        fill="currentColor"
      />
    </svg>
  )
}

function SavedIcon(): JSX.Element {
  return (
    <svg className="h-4 w-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
    </svg>
  )
}

export function SaveStatusIndicator({ status }: SaveStatusIndicatorProps): JSX.Element {
  const containerClass = [
    'flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg border',
    'transition-all duration-300 ease-in-out',
    status === 'idle' ? 'opacity-0 translate-y-2 scale-95' : 'opacity-100 translate-y-0 scale-100',
    status === 'saving'
      ? 'bg-muted border-border'
      : 'bg-green-50 dark:bg-green-900/50 border-green-200 dark:border-green-700',
  ].join(' ')

  return (
    <div className="fixed top-24 right-8 z-50 pointer-events-none">
      <div className={containerClass}>
        {status === 'saving' && (
          <>
            <SavingIcon />
            <span className="text-sm font-medium text-primary">Saving...</span>
          </>
        )}
        {status === 'saved' && (
          <>
            <SavedIcon />
            <span className="text-sm font-medium text-green-700 dark:text-green-300">Saved</span>
          </>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// LogoUploadSection
// =============================================================================

interface LogoUploadSectionProps {
  logoPreview: string | null
  error?: string
  onUpload: (e: ChangeEvent<HTMLInputElement>) => void
  onRemove: () => void
}

interface LogoPreviewProps {
  logoPreview: string
  onRemove: () => void
}

function LogoPreview({ logoPreview, onRemove }: LogoPreviewProps): JSX.Element {
  return (
    <div className="relative">
      <img
        alt="Company Logo"
        className="w-32 h-32 object-contain border-2 border-border rounded-lg bg-surface"
        src={logoPreview}
      />
      <button
        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 flex items-center justify-center shadow-lg"
        title="Remove logo"
        type="button"
        onClick={onRemove}
      >
        ×
      </button>
    </div>
  )
}

function LogoPlaceholder(): JSX.Element {
  return (
    <div className="w-32 h-32 border-2 border-dashed border-border rounded-lg flex items-center justify-center bg-surface-secondary">
      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    </div>
  )
}

interface UploadButtonProps {
  error?: string
  onUpload: (e: ChangeEvent<HTMLInputElement>) => void
}

function UploadButton({ error, onUpload }: UploadButtonProps): JSX.Element {
  return (
    <div className="flex-1">
      <input accept="image/*" className="hidden" id="logo-upload" type="file" onChange={onUpload} />
      <label
        className="inline-flex items-center px-4 py-2 border border-border rounded-lg cursor-pointer hover:bg-surface-secondary transition-colors"
        htmlFor="logo-upload"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
        Upload Logo
      </label>
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        PNG, JPG or GIF. Max 2MB. Recommended: 200x200px
      </p>
      {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  )
}

export function LogoUploadSection({ logoPreview, error, onUpload, onRemove }: LogoUploadSectionProps): JSX.Element {
  return (
    <div>
      <span className="block text-sm font-medium text-foreground-secondary mb-3">Company Logo</span>
      <div className="flex items-start gap-6">
        <div className="flex-shrink-0">
          {logoPreview ? (
            <LogoPreview logoPreview={logoPreview} onRemove={onRemove} />
          ) : (
            <LogoPlaceholder />
          )}
        </div>
        <UploadButton error={error} onUpload={onUpload} />
      </div>
    </div>
  )
}
